import { useState, useEffect } from 'react';
import { ChipTipoTramite } from '../../components/ChipsTramitesCliente';
import { getTramites, getResumenTramites, SIN_CARPETA } from '../../services/tramiteService';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { getTipoTramites } from '../../services/tipoTramiteService';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Pagination } from '@mui/material';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import ModalIniciarTramite from '../../components/ModalIniciarTramite';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import useBreakpoint from '../../hooks/useBreakpoint';
import ListaTarjetas from '../../components/mobile/ListaTarjetas';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { formatCuil } from '../../utils/formatCuil';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
import PageHeader from '../../components/ui/PageHeader';

// Listado unificado de TODOS los tipos de trámite (ART, RTI, Jubilación, PUAM, Plan de Pago,
// Reajuste, Reconocimiento, PNC, Amparo PNC), accesible desde /tramites. ART y RTI conservan
// además su propio listado dedicado (ListadoTramitesART.js / ListadoTramitesRTI.js).
// Sin filtro por Estado (a pedido: solo CUIL, Nombre/Apellido, Organismo y Tipo de Trámite).
function ListadoTramitesUnificado() {
    const [tramites, setTramites] = useState([]);
    const [resumen, setResumen] = useState({ total: 0, enProceso: 0, finalizados: 0 });
    const [page, setPage] = useFiltroPersistente('tramitesUnificado.page', 0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filtroIdCliente, setFiltroIdCliente] = useFiltroPersistente('tramitesUnificado.filtroIdCliente', '');
    const [filtroNombreApellido, setFiltroNombreApellido] = useFiltroPersistente('tramitesUnificado.filtroNombreApellido', '');
    const [filtroOrganismo, setFiltroOrganismo] = useFiltroPersistente('tramitesUnificado.filtroOrganismo', '');
    const [filtroTipoTramite, setFiltroTipoTramite] = useFiltroPersistente('tramitesUnificado.filtroTipoTramite', '');
    // Carpeta física del archivo (catálogo `carpeta`, V202). Reemplaza al listado por ubicación
    // que se había pensado aparte: es la misma pregunta —"¿qué hay en tal carpeta?"— y acá se
    // combina con los filtros que ya existen en vez de vivir en una pantalla suelta.
    const [filtroCarpeta, setFiltroCarpeta] = useFiltroPersistente('tramitesUnificado.filtroCarpeta', '');
    const [carpetas, setCarpetas] = useState([]);
    const [tiposTramite, setTiposTramite] = useState([]);
    const [modalIniciarAbierto, setModalIniciarAbierto] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const navigate = useNavigate();

    useEffect(() => {
        getTipoTramites()
            .then((tipos) => setTiposTramite(Array.isArray(tipos) ? tipos : []))
            .catch(() => setTiposTramite([]));
    }, []);



    const obtenerUltimoComentario = (texto) => {
        if (!texto) return 'Sin comentarios';
        const lineas = texto.split('\n').filter((linea) => linea.trim() !== '');
        return lineas.length > 0 ? lineas[lineas.length - 1] : 'Sin comentarios';
    };

    const cargarTramites = async () => {
        setLoading(true);
        try {
            const data = await getTramites(
                page,
                15,
                filtroIdCliente?.trim() || '',
                filtroTipoTramite || null,
                null, // sin filtro por estado
                filtroNombreApellido?.trim() || '',
                filtroOrganismo || null,
                filtroCarpeta === '' ? null : Number(filtroCarpeta)
            );
            setTramites(data.content || []);
            setTotalPages(data.totalPages || 0);
        } catch (e) {
            console.error('Error al cargar trámites:', e);
            setSnackbar({ open: true, message: 'Error al cargar trámites', severity: 'error' });
        }
        setLoading(false);
    };

    const cargarResumen = async () => {
        try {
            const data = await getResumenTramites(
                filtroIdCliente?.trim() || '',
                filtroNombreApellido?.trim() || '',
                filtroTipoTramite || null,
                filtroOrganismo || null,
                filtroCarpeta === '' ? null : Number(filtroCarpeta)
            );
            setResumen({
                total: data?.total || 0,
                enProceso: data?.enProceso || 0,
                finalizados: data?.finalizados || 0,
            });
        } catch (e) {
            console.error('Error al cargar resumen de trámites:', e);
        }
    };

    // Cada vez que cambie paginación/filtros, recargamos desde servidor (los filtros se combinan
    // todos entre sí vía AND en el backend: cuil + nombre/apellido + organismo + tipo).
    useEffect(() => {
        cargarTramites();
        cargarResumen();
    }, [page, filtroIdCliente, filtroTipoTramite, filtroNombreApellido, filtroOrganismo, filtroCarpeta]);

    // Solo las carpetas activas: una carpeta dada de baja no se ofrece para filtrar. Si algún
    // trámite viejo la tiene asignada sigue apareciendo en "Todas", no se pierde.
    useEffect(() => {
        DatosMaestrosService.getCarpetas(true)
            .then((res) => setCarpetas(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setCarpetas([]));
    }, []);

    const editarTramite = (tramite) => navigate(rutaDetalleTramite(tramite));
    // El clic en la fila abre el detalle, salvo que la persona esté seleccionando texto (por ejemplo
    // para copiar un CUIL o un comentario): ahí arrastrar el mouse no debe sacarla de la pantalla.
    const abrirDesdeFila = (tramite) => {
        if (window.getSelection?.().toString()) return;
        editarTramite(tramite);
    };

    const ORGANISMO_LABELS = { PROVINCIA: 'Provincia', ANSES: 'ANSES', ANDIS: 'ANDIS' };

    const limpiarFiltros = () => {
        setPage(0);
        setFiltroIdCliente('');
        setFiltroNombreApellido('');
        setFiltroOrganismo('');
        setFiltroTipoTramite('');
        setFiltroCarpeta('');
    };

    const chipsFiltros = [
        filtroIdCliente && { key: 'cuil', label: `CUIL: ${filtroIdCliente}`, onQuitar: () => { setPage(0); setFiltroIdCliente(''); } },
        filtroNombreApellido && { key: 'nombre', label: filtroNombreApellido, onQuitar: () => { setPage(0); setFiltroNombreApellido(''); } },
        filtroOrganismo && { key: 'organismo', label: ORGANISMO_LABELS[filtroOrganismo] || filtroOrganismo, onQuitar: () => { setPage(0); setFiltroOrganismo(''); } },
        filtroTipoTramite && {
            key: 'tipo',
            label: tiposTramite.find((t) => String(t.idTipoTramite) === String(filtroTipoTramite))?.nombre || 'Tipo',
            onQuitar: () => { setPage(0); setFiltroTipoTramite(''); },
        },
        filtroCarpeta && {
            key: 'carpeta',
            label: filtroCarpeta === SIN_CARPETA
                ? 'Sin carpeta asignada'
                : (carpetas.find((c) => String(c.id) === String(filtroCarpeta))?.nombre || 'Carpeta'),
            onQuitar: () => { setPage(0); setFiltroCarpeta(''); },
        },
    ].filter(Boolean);

    // Contenido de cada campo sin el wrapper de grilla de escritorio — se reusa apilado
    // dentro del BottomSheet de mobile sin duplicar el markup de cada input/select.
    const campoCuil = (
        <>
            <label className="form-label" >
                <i className="bi bi-search me-2"></i>Buscar por CUIL
            </label>
            <div className="input-group">
                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                    <i className="bi bi-card-text" style={{ color: '#6B7280' }}></i>
                </span>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Ingrese CUIL del cliente"
                    value={filtroIdCliente}
                    maxLength="13"
                    onChange={(e) => { setPage(0); setFiltroIdCliente(formatCuil(e.target.value)); }}
                    style={{ borderLeft: 'none' }}
                />
            </div>
        </>
    );
    const campoNombre = (
        <>
            <label className="form-label" >
                <i className="bi bi-person me-2"></i>Buscar por Nombre/Apellido
            </label>
            <div className="input-group">
                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                    <i className="bi bi-search" style={{ color: '#6B7280' }}></i>
                </span>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Nombre o apellido del cliente"
                    value={filtroNombreApellido}
                    onChange={(e) => { setPage(0); setFiltroNombreApellido(e.target.value); }}
                    style={{ borderLeft: 'none' }}
                />
            </div>
        </>
    );
    const campoOrganismo = (
        <>
            <label className="form-label" >
                <i className="bi bi-building me-2"></i>Organismo
            </label>
            <div className="input-group">
                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                    <i className="bi bi-bank" style={{ color: '#6B7280' }}></i>
                </span>
                <select
                    className="form-select"
                    value={filtroOrganismo}
                    onChange={(e) => { setPage(0); setFiltroOrganismo(e.target.value); }}
                    style={{ borderLeft: 'none' }}
                >
                    <option value="">Todos</option>
                    <option value="PROVINCIA">Provincia</option>
                    <option value="ANSES">ANSES</option>
                    <option value="ANDIS">ANDIS</option>
                </select>
            </div>
        </>
    );
    const campoTipo = (
        <>
            <label className="form-label" >
                <i className="bi bi-tag me-2"></i>Tipo de Trámite
            </label>
            <div className="input-group">
                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                    <i className="bi bi-folder2" style={{ color: '#6B7280' }}></i>
                </span>
                <select
                    className="form-select"
                    value={filtroTipoTramite}
                    onChange={(e) => { setPage(0); setFiltroTipoTramite(e.target.value); }}
                    style={{ borderLeft: 'none' }}
                >
                    <option value="">Todos los tipos</option>
                    {tiposTramite.map((tipo) => (
                        <option key={tipo.idTipoTramite} value={tipo.idTipoTramite}>{tipo.nombre}</option>
                    ))}
                </select>
            </div>
        </>
    );
    const campoCarpeta = (
        <>
            <label className="form-label" >
                <i className="bi bi-archive me-2"></i>Carpeta / Ubicación
            </label>
            <div className="input-group">
                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                    <i className="bi bi-inboxes" style={{ color: '#6B7280' }}></i>
                </span>
                <select
                    className="form-select"
                    value={filtroCarpeta}
                    onChange={(e) => { setPage(0); setFiltroCarpeta(e.target.value); }}
                    style={{ borderLeft: 'none' }}
                >
                    <option value="">Todas las carpetas</option>
                    {/* La búsqueda que más se hace: qué papeles todavía no se archivaron. */}
                    <option value={SIN_CARPETA}>— Sin carpeta asignada —</option>
                    {carpetas.map((carpeta) => (
                        <option key={carpeta.id} value={carpeta.id}>
                            {carpeta.nombre}
                            {carpeta.ubicacion && carpeta.ubicacion !== carpeta.nombre ? ` (${carpeta.ubicacion})` : ''}
                            {/* Cuántos papeles hay ahí: evita elegir una carpeta y encontrarla vacía. */}
                            {carpeta.cantidadTramites != null ? ` · ${carpeta.cantidadTramites}` : ''}
                        </option>
                    ))}
                </select>
            </div>
        </>
    );

    // listado-pagina: el .container del MainLayout usa el ancho disponible (hasta 1400 px) en vez de
    // quedarse en el tope de Bootstrap (1092 px a 1366 de ventana); sin esto la tabla, que pide
    // ~1200 px, hacía scroll horizontal. Es el mismo mecanismo de los Listados (bootstrap-tema.css).
    return (
        <div className="area-general listado-pagina">
            <div className="container-fluid mt-4">
                <PageHeader
                    variante="plano"
                    icono="journal-richtext"
                    titulo="Trámites"
                    descripcion="Listado unificado de todos los trámites"
                    accion={
                        <button
                            className="btn d-flex align-items-center gap-2"
                            style={{ background: 'var(--area-solid)', color: '#fff', borderColor: 'var(--area-solid)' }}
                            onClick={() => setModalIniciarAbierto(true)}
                        >
                            <i className="bi bi-plus-circle-fill" aria-hidden="true"></i>
                            Iniciar Trámite
                        </button>
                    }
                />

                {/* Estadísticas */}
                <div className="row g-3 mb-4 mob-grid-2col">
                    <div className="col-md-4 mob-grid-full">
                        <TarjetaKpi label="Total trámites" valor={resumen.total} icono="folder2-open" />
                    </div>
                    <div className="col-md-4">
                        <TarjetaKpi tono="advertencia" label="En proceso" valor={resumen.enProceso} icono="hourglass-split" />
                    </div>
                    <div className="col-md-4">
                        <TarjetaKpi tono="exito" label="Finalizados" valor={resumen.finalizados} icono="check-circle-fill" />
                    </div>
                </div>

                {/* Filtros: CUIL, Nombre/Apellido, Organismo, Tipo de Trámite (sin Estado) */}
                {esMobile ? (
                    <div className="mb-4">
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                <div className="mb-3">{campoCuil}</div>
                                <div className="mb-3">{campoNombre}</div>
                                <div className="mb-3">{campoOrganismo}</div>
                                <div className="mb-3">{campoTipo}</div>
                                <div>{campoCarpeta}</div>
                            </BottomSheet>
                        )}
                    </div>
                ) : (
                    <div className="card shadow-sm mb-4" style={{ border: 'none', borderRadius: '12px' }}>
                        <div className="card-body" style={{ padding: '1.5rem' }}>
                            <div className="row g-3 mob-grid-2col">
                                <div className="col-lg-6 col-md-6">{campoCuil}</div>
                                <div className="col-lg-6 col-md-6">{campoNombre}</div>
                                <div className="col-lg-4 col-md-6">{campoOrganismo}</div>
                                <div className="col-lg-3 col-md-6">{campoTipo}</div>
                                <div className="col-lg-3 col-md-6">{campoCarpeta}</div>
                            </div>

                            {/* Botón limpiar filtros */}
                            <div className="text-end mt-3">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={limpiarFiltros}
                                    style={{ borderRadius: '8px', padding: '0.5rem 1.5rem', fontWeight: '500' }}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>Limpiar filtros
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {esMobile ? (
                    <>
                        <ListaTarjetas
                            items={tramites}
                            loading={loading}
                            keyExtractor={(t) => t.idTramite}
                            textoVacio="No se encontraron trámites."
                            renderTarjeta={(t) => {
                                const colorEstado = t.nombreEstadoActual === 'Finalizado'
                                    ? { bg: '#dcfce7', color: '#166534' }
                                    : t.nombreEstadoActual === 'Rechazado'
                                        ? { bg: '#fee2e2', color: '#991b1b' }
                                        : { bg: '#fef3c7', color: '#92400e' };
                                const colorOrganismo = t.organismo === 'ANSES'
                                    ? { bg: '#dbeafe', color: '#1e40af' }
                                    : t.organismo === 'PROVINCIA'
                                        ? { bg: '#fef3c7', color: '#92400e' }
                                        : t.organismo === 'ANDIS'
                                            ? { bg: '#ede9fe', color: '#5b21b6' }
                                            : null;
                                return (
                                    <>
                                        <div className="mob-tarjeta-header">
                                            <div>
                                                <div className="mob-tarjeta-titulo">
                                                    {[t.apellidoCliente, t.nombreCliente].filter(Boolean).join(', ') || 'N/A'}
                                                </div>
                                                <div className="mob-tarjeta-subtitulo">{formatCuil(t.cuilCliente)}</div>
                                            </div>
                                        </div>
                                        <div className="mob-tarjeta-badges">
                                            <span className="mob-tarjeta-badge" style={{ background: colorEstado.bg, color: colorEstado.color }}>
                                                {t.nombreEstadoActual}
                                            </span>
                                            {colorOrganismo && (
                                                <span className="mob-tarjeta-badge" style={{ background: colorOrganismo.bg, color: colorOrganismo.color }}>
                                                    {t.organismo}
                                                </span>
                                            )}
                                            {t.esNulidad && (
                                                <span className="mob-tarjeta-badge" style={{ background: '#d97706', color: '#fff', fontWeight: 700 }}>
                                                    <i className="bi bi-arrow-repeat me-1"></i>Nulidad
                                                </span>
                                            )}
                                        </div>
                                        <div className="mob-tarjeta-meta">
                                            <span>{t.titulo || t.tipoTramite || 'N/A'}</span>
                                            <span>
                                                <i className="bi bi-calendar-event" aria-hidden="true" />
                                                {t.fechaEstadoActual ? new Date(t.fechaEstadoActual).toLocaleDateString('es-ES') : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="mob-tarjeta-acciones">
                                            <button
                                                type="button"
                                                className="mob-tarjeta-accion primaria mob-tocable"
                                                onClick={() => editarTramite(t)}
                                            >
                                                <i className="bi bi-eye" aria-hidden="true" />
                                                Ver detalles
                                            </button>
                                        </div>
                                    </>
                                );
                            }}
                        />
                        {!loading && totalPages > 0 && (
                            <div className="d-flex justify-content-center py-3">
                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={(_e, value) => setPage(value - 1)}
                                    color="primary"
                                />
                            </div>
                        )}
                    </>
                ) : loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                        <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámites...</p>
                    </div>
                ) : (
                    <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
                        <div className="table-responsive tabla-listado-contenedor">
                            <table className="table table-hover align-middle mb-0 tabla-listado-ajustada">
                                <thead style={{ background: 'var(--gradient-primary-light)', borderBottom: '2px solid #3B82F6' }}>
                                    <tr>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>CUIL Cliente</th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>Nombre</th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>Tipo</th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>Estado</th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-calendar-event me-2"></i>Fecha
                                        </th>
                                        <th className="text-start col-comentarios" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', minWidth: '100px' }}>
                                            <i className="bi bi-chat-text me-2"></i>Comentarios
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tramites.map((t) => (
                                        <tr
                                            key={t.idTramite}
                                            tabIndex={0}
                                            title="Abrir el detalle del trámite"
                                            onClick={() => abrirDesdeFila(t)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); editarTramite(t); } }}
                                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                            <td className="text-center" style={{ padding: '1rem' }}>
                                                <span className="badge" style={{ background: '#eff6ff', color: '#1e40af', padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem' }}>
                                                    {formatCuil(t.cuilCliente)}
                                                </span>
                                            </td>
                                            <td className="text-center col-nombre" style={{ padding: '1rem', fontWeight: '500', color: '#374151' }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                                    <span>{[t.apellidoCliente, t.nombreCliente].filter(Boolean).join(', ') || 'N/A'}</span>
                                                    {t.organismo === 'ANSES' && (
                                                        <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>ANSES</span>
                                                    )}
                                                    {t.organismo === 'PROVINCIA' && (
                                                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Provincia</span>
                                                    )}
                                                    {t.organismo === 'ANDIS' && (
                                                        <span style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>ANDIS</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem', color: '#6B7280' }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                                    <ChipTipoTramite tipo={t.tipoTramite} titulo={t.titulo || undefined} />
                                                    {t.esNulidad && (
                                                        <span style={{ background: '#d97706', color: '#fff', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                            <i className="bi bi-arrow-repeat me-1"></i>Nulidad
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center col-estado" style={{ padding: '1rem' }}>
                                                <span
                                                    className="badge"
                                                    style={{
                                                        background: t.nombreEstadoActual === 'Finalizado' ? '#dcfce7' :
                                                            t.nombreEstadoActual === 'Rechazado' ? '#fee2e2' : '#fef3c7',
                                                        color: t.nombreEstadoActual === 'Finalizado' ? '#166534' :
                                                            t.nombreEstadoActual === 'Rechazado' ? '#991b1b' : '#92400e',
                                                        padding: '0.5rem 0.8rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {t.nombreEstadoActual}
                                                </span>
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem', color: '#6B7280' }}>
                                                {t.fechaEstadoActual ? new Date(t.fechaEstadoActual).toLocaleDateString('es-ES') : 'N/A'}
                                            </td>
                                            <td
                                                className="col-comentarios" style={{ padding: '1rem', wordBreak: 'break-word', color: '#6B7280', fontSize: '0.9rem' }}
                                                title={t.comentario || t.comentarios || 'Sin comentarios'}
                                            >
                                                {obtenerUltimoComentario(t.comentario || t.comentarios)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {tramites.length === 0 && (
                                <div className="text-center py-5">
                                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
                                    <p className="mt-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>No se encontraron trámites.</p>
                                </div>
                            )}

                            {totalPages > 0 && (
                                <div className="d-flex justify-content-center py-3 border-top">
                                    <Pagination
                                        count={totalPages}
                                        page={page + 1}
                                        onChange={(_e, value) => setPage(value - 1)}
                                        color="primary"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <ModalIniciarTramite
                open={modalIniciarAbierto}
                onClose={() => setModalIniciarAbierto(false)}
            />
        </div>
    );
}

export default ListadoTramitesUnificado;
